import { Form, Input, InputNumber, notification, Select } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { useCreateClinic } from 'lib/hooks/tanstack/useCreateClinic';
import { useInviteClinicAdmin } from 'lib/hooks/tanstack/useInviteClinicAdmin';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { FormQuestion } from 'lib/types/form';
import { useErrorList } from 'lib/utils/useErrorList';
import React from 'react';

const clinicDetailsQuestions: FormQuestion[] = [
  {
    name: 'name',
    label: 'Organization name',
    component: () => <Input />,
    rules: [{ required: true, message: 'Please input the organisation name' }],
    extra: 'The official name of your organisation'
  },
  {
    name: 'contactEmail',
    label: 'Organization contact email',
    component: () => <Input />,
    rules: [
      {
        required: true,
        message: 'Please input the organisation contact email'
      },
      { type: 'email', message: 'The input is not valid E-mail' }
    ],
    extra: 'The main email address for organisation communications'
  },
  {
    name: 'contactPhoneNumber',
    label: 'Organization contact phone number',
    component: () => <Input />,
    rules: [
      {
        required: true,
        message: 'Please input the organisation contact phone number'
      }
    ],
    extra: 'The primary phone number for organisation inquiries'
  },

  {
    name: 'country',
    label: 'Country',
    component: () => (
      <Select
        options={[
          {
            value: 'CA',
            label: 'CA (Canada)'
          },
          { value: 'US', label: 'US (United States of America)' }
        ]}
      />
    ),
    rules: [{ required: true }]
  },
  {
    name: 'city',
    label: 'City',
    component: () => <Input placeholder="City" />,
    rules: [{ required: true, message: 'Please input your city.' }]
  },
  {
    name: ['billing', 'address'],
    label: 'Organization billing address',
    component: () => <Input />,
    rules: [{ required: true, message: 'Please input the billing address' }],
    extra: 'The address used for billing and invoicing'
  },
  {
    name: ['billing', 'primaryContact', 'name'],
    label: 'Organization billing primary contact name',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the primary contact name' }
    ],
    extra: 'The name of the primary contact for billing matters'
  },
  {
    name: ['billing', 'primaryContact', 'email'],
    label: 'Billing primary contact email',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the primary contact email' },
      { type: 'email', message: 'The input is not valid E-mail' }
    ],
    extra: 'The email address of the primary contact for billing'
  },
  {
    name: 'numberOfUsers',
    label: 'Number of users',
    component: () => <InputNumber min={1} style={{ width: '100%' }} />,
    rules: [{ required: true, message: 'Please input the number of users' }],
    extra: 'How many users will need access to the system'
  },
  {
    name: 'estimatedNumberOfTestTakers',
    label: 'Estimated number of test-takers per year',
    component: () => <InputNumber min={0} style={{ width: '100%' }} />,
    rules: [
      {
        required: true,
        message: 'Please input the estimated number of test-takers'
      }
    ],
    extra: 'Approximate number of patients taking assessments annually'
  }
];

const clinicAdminQuestions: FormQuestion[] = [
  {
    name: ['clinicAdmin', 'firstName'],
    label: 'Organization admin first name',
    component: () => <Input />,
    rules: [
      {
        required: true,
        message: 'Please input the Organization admin first name'
      }
    ],
    extra: 'The first name of the organisation administrator'
  },
  {
    name: ['clinicAdmin', 'lastName'],
    label: 'Organization admin last name',
    component: () => <Input />,
    rules: [
      {
        required: true,
        message: 'Please input the Organization admin last name'
      }
    ],
    extra: 'The last name of the organisation administrator'
  },
  {
    name: ['clinicAdmin', 'email'],
    label: 'Organization admin email',
    component: () => <Input />,
    rules: [
      { required: true, message: 'Please input the Organization admin email' },
      { type: 'email', message: 'The input is not valid E-mail' }
    ],
    extra: 'The email address of the organisation administrator'
  }
];

interface AddOrganizationDrawerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

interface FormValues {
  name: string;
  contactEmail: string;
  contactPhoneNumber: string;
  country: 'CA' | 'US';
  city: string;
  billing: {
    address: string;
    primaryContact: {
      name: string;
    };
  };
  numberOfUser: number;
  estimatedNumberOfTestTakers: number;
  clinicAdmin: {
    firstName: string;
    lastName: string;
    email: string;
  };
  faxNumber?: string;
}

export const AddOrganizationDrawer: React.FC<AddOrganizationDrawerProps> = ({
  onCancel,
  onSuccess
}) => {
  const [form] = Form.useForm<FormValues>();
  const createClinicMutation = useCreateClinic();
  const inviteClinicAdminMutation = useInviteClinicAdmin();
  const errorList = useErrorList();

  useQueryErrorNotifier(createClinicMutation, errorList, 'CreateClinic')
  useQueryErrorNotifier(inviteClinicAdminMutation, errorList, 'InviteClinicAdmin')

  async function onFinish(values: FormValues) {
    try {
      // 1. Create Clinic

      const createClinicResponse = await createClinicMutation.mutateAsync({
        name: values.name,
        country: values.country ?? 'canada', // Assuming a default or that these will be added to the form later
        address: values.billing.address,
        phoneNumber: values.contactPhoneNumber,
        faxNumber: values.faxNumber || 'N/A',
        addressLine2: null,
        billing: {
          address: null,
          primaryContact: {
            email: null,
            name: null
          }
        },
        city: values.city,
        contactEmail: null,
        crx_settings: {
        },
        customerType: 'standard',
        organisationMetadata: {
          estimatedTestTakerCount: null,
          userCount: null,
          businessType: null
        },
        postalOrZipCode: null,
        provinceOrState: null
      });
      const clinicId = createClinicResponse.data.id;

      // 2. Invite Clinic Admin
      const clinicAdminPayload = {
        clinicId: clinicId,
        firstName: values.clinicAdmin.firstName,
        lastName: values.clinicAdmin.lastName,
        email: values.clinicAdmin.email
      };

      await inviteClinicAdminMutation.mutateAsync(clinicAdminPayload);

      notification.success({
        message: 'Success',
        description: 'Organization created and admin invited successfully'
      });
      onSuccess(); // Close modal and refresh table
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description:
          error.message || 'Failed to create organization or invite admin.'
      });
      console.error('Submission error:', error);
      errorList.addError(error);
    }
  }

  const isSubmitting =
    createClinicMutation.isPending || inviteClinicAdminMutation.isPending;

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <h2>Organization Details</h2>
      {clinicDetailsQuestions.map(question => (
        <Form.Item
          key={JSON.stringify(question.name)}
          name={question.name}
          label={question.label}
          rules={question.rules}
          extra={question.extra}
        >
          {question.component()}
        </Form.Item>
      ))}

      <h2>Organization Admin Details</h2>
      {clinicAdminQuestions.map(question => (
        <Form.Item
          key={JSON.stringify(question.name)}
          name={question.name}
          label={question.label}
          rules={question.rules}
          extra={question.extra}
        >
          {question.component()}
        </Form.Item>
      ))}

      <Form.Item>
        <BaseButton type="primary" htmlType="submit" loading={isSubmitting}>
          Create Organization and Invite Admin
        </BaseButton>
        <BaseButton
          type="text"
          onClick={onCancel}
          style={{ marginLeft: 8 }}
          disabled={isSubmitting}
        >
          Cancel
        </BaseButton>
      </Form.Item>

      <ErrorListDisplay errorList={errorList} />
    </Form>
  );
};
